import { create } from 'zustand';

import normalizeList from 'utils/normalizeList';

type TradingContextEntry = {
    portfolioActive: boolean;
    eventTickers: string[];
    orderEventTickers: string[];
};

interface TradingContextActions {
    setContext: (id: string, entry: TradingContextEntry) => void;
    clearContext: (id: string) => void;
}

interface TradingContextState {
    contexts: Map<string, TradingContextEntry>;
    actions: TradingContextActions;
}

/**
 * Registry of "I need live trading data" declarations from mounted components
 * (portfolio pages, event pages), keyed by a caller-owned id. Read imperatively
 * via `getState()` by `stores/tradingSync.ts` and by `userOrdersStore`
 * to gate WS-driven refreshes — no React component renders off this state.
 */
export const useTradingContextStore = create<TradingContextState>()((set) => ({
    contexts: new Map(),
    actions: {
        setContext: (id, entry) =>
            set((state) => {
                const contexts = new Map(state.contexts);
                contexts.set(id, {
                    portfolioActive: !!entry.portfolioActive,
                    eventTickers: normalizeList(entry.eventTickers),
                    orderEventTickers: normalizeList(entry.orderEventTickers),
                });
                return { contexts };
            }),
        clearContext: (id) =>
            set((state) => {
                if (!state.contexts.has(id)) {
                    return state;
                }
                const contexts = new Map(state.contexts);
                contexts.delete(id);
                return { contexts };
            }),
    },
}));

const getActiveEventTickers = (state: TradingContextState): string[] => {
    const tickers = new Set<string>();
    for (const entry of state.contexts.values()) {
        entry.eventTickers.forEach((ticker) => tickers.add(ticker));
    }
    return Array.from(tickers);
};

const getActiveOrderEventTickers = (state: TradingContextState): string[] => {
    const eventTickers = new Set<string>();
    for (const entry of state.contexts.values()) {
        entry.orderEventTickers.forEach((eventTicker) => eventTickers.add(eventTicker));
    }
    return Array.from(eventTickers);
};

const getPortfolioActive = (state: TradingContextState): boolean => {
    for (const entry of state.contexts.values()) {
        if (entry.portfolioActive) {
            return true;
        }
    }
    return false;
};

export const tradingContextSelectors = {
    portfolioActive: getPortfolioActive,
    activeEventTickers: getActiveEventTickers,
    activeOrderEventTickers: getActiveOrderEventTickers,
    hasEventTicker: (state: TradingContextState, eventTicker: string): boolean =>
        getActiveEventTickers(state).includes(eventTicker),
    hasOrderEventTicker: (state: TradingContextState, eventTicker: string): boolean =>
        getActiveOrderEventTickers(state).includes(eventTicker),
    contextKey: (state: TradingContextState): string => {
        const events = getActiveEventTickers(state).sort().join(',');
        const eventTickers = getActiveOrderEventTickers(state).sort().join(',');
        return `portfolio:${getPortfolioActive(state)}|events:${events}|orders:${eventTickers}`;
    },
};

export type { TradingContextEntry };
